<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import BaseModal from '@/components/BaseModal.vue'
import type { ICatalogoPui } from './puiCatalogos'

const props = defineProps<{
	modelValue: boolean
	catalogo: ICatalogoPui | null
	valorRegistrado?: string
}>()

const emit = defineEmits<{
	'update:modelValue': [value: boolean]
}>()

const busqueda = ref('')

watch(
	() => props.modelValue,
	visible => {
		if (visible) busqueda.value = ''
	}
)

const entradasFiltradas = computed(() => {
	const entradas = props.catalogo?.entradas ?? []
	const term = busqueda.value.trim().toLowerCase()
	if (!term) return entradas
	return entradas.filter(entrada => entrada.clave.toLowerCase().includes(term) || entrada.significado.toLowerCase().includes(term))
})

const cerrar = () => emit('update:modelValue', false)

const valorNormalizado = computed(() => (props.valorRegistrado ?? '').trim())

const coincideClave = (clave: string): boolean => valorNormalizado.value.length > 0 && clave.trim().toLowerCase() === valorNormalizado.value.toLowerCase()
</script>

<template>
	<div class="pui-catalogo-modal">
		<BaseModal :model-value="modelValue" :title="catalogo?.titulo ?? 'Catálogo'" message="" :test-mode="false" size="large" @update:model-value="emit('update:modelValue', $event)">
			<template #body>
				<div class="pui-catalogo">
					<p v-if="valorNormalizado" class="pui-catalogo__captured">
						Capturaste: <code>{{ valorNormalizado }}</code>
					</p>

					<div class="pui-catalogo__search">
						<VaIcon name="search" size="18px" />
						<input
							id="pui-catalogo-search"
							v-model="busqueda"
							type="search"
							class="pui-catalogo__input"
							placeholder="Escribe para buscar en el catálogo..."
							autocomplete="off"
							aria-label="Buscar en el catálogo" />
					</div>

					<div class="pui-catalogo__table-wrap">
						<table class="pui-catalogo__table">
							<thead>
								<tr>
									<th scope="col">Clave</th>
									<th v-if="catalogo?.titulo !== 'Opción educativa'" scope="col">¿Qué significa?</th>
									<th v-else scope="col">¿Qué modalidad educativa?</th>
								</tr>
							</thead>
							<tbody>
								<template v-if="entradasFiltradas.length === 0">
									<tr>
										<td colspan="2" class="pui-catalogo__empty">No hay coincidencias en el catálogo.</td>
									</tr>
								</template>
								<template v-else>
									<tr v-for="(entrada, index) in entradasFiltradas" :key="`${entrada.clave}-${index}`" :class="{ 'pui-catalogo__row--match': coincideClave(entrada.clave) }">
										<td>
											<code class="pui-catalogo__clave">{{ entrada.clave }}</code>
										</td>
										<td>{{ entrada.significado }}</td>
									</tr>
								</template>
							</tbody>
						</table>
					</div>

					<p class="pui-catalogo__hint">En tu Excel debes capturar la <strong>clave</strong> (la columna de la izquierda), tal cual, con sus ceros.</p>
				</div>
			</template>
			<template #footer>
				<button type="button" class="btn btn-primary" @click="cerrar">Cerrar</button>
			</template>
		</BaseModal>
	</div>
</template>

<style scoped>
.pui-catalogo {
	display: flex;
	flex-direction: column;
	gap: 0.85rem;
}
.pui-catalogo__search {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	padding: 0.55rem 0.75rem;
	border: 1px solid rgba(6, 35, 66, 0.16);
	border-radius: 10px;
	background: #f8fafc;
	color: #6b7280;
}
.pui-catalogo__input {
	flex: 1;
	min-width: 0;
	border: none;
	background: transparent;
	font-size: 0.875rem;
	color: #062342;
	outline: none;
}
.pui-catalogo__table-wrap {
	max-height: 360px;
	overflow: auto;
	border: 1px solid #e5e7eb;
	border-radius: 10px;
}
.pui-catalogo__table {
	width: 100%;
	border-collapse: collapse;
}
.pui-catalogo__table thead th {
	position: sticky;
	top: 0;
	padding: 0.55rem 0.75rem;
	background: #062342;
	color: #fff;
	font-size: 0.68rem;
	font-weight: 700;
	letter-spacing: 0.05em;
	text-transform: uppercase;
	text-align: left;
}
.pui-catalogo__table tbody td {
	padding: 0.5rem 0.75rem;
	border-bottom: 1px solid #eef2f7;
	font-size: 0.8125rem;
	color: #374151;
	vertical-align: top;
}
.pui-catalogo__table tbody tr:nth-child(even) {
	background: #f8fafc;
}
.pui-catalogo__clave {
	display: inline-block;
	padding: 0.15rem 0.4rem;
	border-radius: 6px;
	background: #f1f5f9;
	font-weight: 700;
	color: #0f172a;
}
.pui-catalogo__empty {
	text-align: center;
	color: #6b7280;
}
.pui-catalogo__hint {
	margin: 0;
	font-size: 0.78rem;
	line-height: 1.5;
	color: #6b7280;
}
.pui-catalogo__hint strong {
	color: #062342;
}
.pui-catalogo__captured {
	margin: 0;
	padding: 0.55rem 0.75rem;
	border-radius: 10px;
	background: #fef2f2;
	border: 1px solid #fecaca;
	font-size: 0.82rem;
	color: #991b1b;
}
.pui-catalogo__captured code {
	padding: 0.05rem 0.35rem;
	border-radius: 4px;
	background: #fff;
	color: #0f172a;
	font-weight: 700;
}
.pui-catalogo__table tbody tr.pui-catalogo__row--match {
	background: #ecfdf5;
}
.pui-catalogo__table tbody tr.pui-catalogo__row--match td {
	color: #14532d;
	font-weight: 600;
}
</style>
